import { BRAND } from "@/brand";
import PublicFooter from "@/components/public/PublicFooter";
import PublicHeader from "@/components/public/PublicHeader";
import WhatsAppButton from "@/components/public/WhatsAppButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/contexts/LanguageContext";
import { getDict } from "@/lib/i18n";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowRight,
  Gauge,
  HelpCircle,
  Mail,
  MapPin,
  MessageCircle
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";

// Fotografías reales del laboratorio Golf Motion Lab (mayo 2026)
const IMG_HERO_SWING = "/img/gml-real-hero-trackman-banner_407da08a.jpg";
const IMG_METHODOLOGY = "/img/gml-real-biomech-skeleton_31831aa5.jpg";
const IMG_TECHNOLOGY = "/img/gml-real-trackman-tablet_bbd70659.jpg";
const IMG_TEAM_GROUP = "/img/gml-team-group_022e6252.jpg";
const IMG_PROCESS_TEAM = "/img/gml-team-lab-analysis_ef878cf4.jpeg";

const COLLABORATORS = [
  {
    name: "Golf Las Pinaillas",
    href: "https://clubdegolflaspinaillas.com",
    logo: "/img/golf-las-pinaillas-logo_c1477391.jpg",
  },
  {
    name: "Federación de Golf de Castilla-La Mancha",
    href: "https://fedgolfclm.com",
    logo: "/img/fed-golf-clm-logo_d02d7621.png",
  },
  {
    name: "VALD Performance",
    href: "https://valdperformance.com/es",
    logo: "/img/vald-performance-logo_9a6cb9b2.jpeg",
  },
];

export default function Home() {
  const { lang } = useLang();
  const t = getDict(lang);
  const [submitting, setSubmitting] = useState(false);

  type PlayerProfile = "" | "estancado" | "dolor" | "competidor" | "profesional" | "otro";
  const [form, setForm] = useState<{
    name: string;
    email: string;
    phone: string;
    handicap: string;
    playerProfile: PlayerProfile;
    message: string;
    consent: boolean;
  }>({
    name: "",
    email: "",
    phone: "",
    handicap: "",
    playerProfile: "",
    message: "",
    consent: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.consent) {
      toast.error(t.contact.form.errorIncomplete);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("https://submit-form.com/vsJ92hS3C", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("submit failed");
      toast.success(t.contact.form.success);
      setForm({
        name: "",
        email: "",
        phone: "",
        handicap: "",
        playerProfile: "",
        message: "",
        consent: false,
      });
    } catch {
      toast.error(t.contact.form.errorSend);
    } finally {
      setSubmitting(false);
    }
  };

  // Reliable scroll-to-hash on load (handles cross-page navigation from /equipo).
  // Images load progressively and shift layout, so we retry the scroll a few times.

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      attempts += 1;
      if (attempts < 6) {
        window.setTimeout(tryScroll, 350);
      }
    };
    const initial = window.setTimeout(tryScroll, 100);
    return () => window.clearTimeout(initial);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <PublicHeader />

      {/* ============================================
          HERO
      ============================================ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[var(--cream)]">
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 0%, oklch(0.85 0.06 150 / 0.25), transparent 50%), radial-gradient(circle at 90% 100%, oklch(0.92 0.04 85 / 0.45), transparent 50%)",
            }}
          />
        </div>

        <div className="container pt-16 md:pt-24 pb-20 md:pb-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--forest)]/8 border border-[var(--forest)]/15 mb-8">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--forest)]" />
                <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-[var(--forest)]">
                  {t.hero.badge}
                </span>
              </div>

              <h1
                className="font-display font-light text-[var(--forest-deep)] leading-[0.95] tracking-[-0.035em]"
                style={{ fontSize: "clamp(2.8rem, 7.5vw, 6.25rem)" }}
              >
                {t.hero.titleLine1}<br />
                <span className="italic text-[var(--forest)]">{t.hero.titleLine2}</span><br />
                {t.hero.titleLine3}
              </h1>

              <p className="mt-8 max-w-xl text-lg text-[var(--forest-deep)]/75 leading-relaxed">
                {t.hero.paragraph}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a
                  href="#contacto"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[var(--forest)] text-[var(--cream)] rounded-full font-semibold tracking-wide hover:bg-[var(--forest-deep)] transition-colors"
                >
                  {t.hero.ctaPrimary}
                  <ArrowRight size={18} />
                </a>
                <a
                  href="#metodologia"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[var(--forest)] text-[var(--forest)] rounded-full font-semibold tracking-wide hover:bg-[var(--forest)]/5 transition-colors"
                >
                  {t.hero.ctaSecondary}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-4 bg-gradient-to-br from-[var(--forest)]/15 to-transparent rounded-3xl blur-2xl" />
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-[0_30px_80px_-30px_rgb(15,61,35,0.35)]">
                  <img
                    src={IMG_HERO_SWING}
                    alt={t.hero.heroImgAlt}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[var(--forest-deep)]/40 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
                    <img
                      src={BRAND.logoGreenStacked}
                      alt="Golf Motion Lab"
                      className="w-16 opacity-90 drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)]"
                      draggable={false}
                    />
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--cream)]/85">
                      {t.hero.labChip}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Value stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 pt-12 border-t border-[var(--forest-deep)]/10">
            {t.stats.map((s) => (
              <div key={s.label}>
                <div className="font-display text-4xl md:text-5xl font-light text-[var(--forest)] leading-none tracking-tight">
                  {s.num}
                </div>
                <div className="mt-3 text-xs font-semibold tracking-[0.12em] uppercase text-[var(--forest-deep)]/60">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          METODOLOGÍA
      ============================================ */}
      <section id="metodologia" className="py-24 md:py-32" style={{ scrollMarginTop: 96 }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-4">
                {t.methodology.eyebrow}
              </div>
              <h2
                className="font-display font-light text-[var(--forest-deep)] leading-[1.05] tracking-[-0.02em]"
                style={{ fontSize: "clamp(2.25rem, 5vw, 3.75rem)" }}
              >
                {t.methodology.titleA}
                <span className="italic text-[var(--forest)]">{t.methodology.titleEm}</span>
                {t.methodology.titleB}
              </h2>
              <p className="mt-6 text-lg text-[var(--forest-deep)]/70 leading-relaxed max-w-xl">
                {t.methodology.intro}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_20px_60px_-25px_rgb(15,61,35,0.3)]">
                <img
                  src={IMG_METHODOLOGY}
                  alt={t.methodology.imgAlt}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--forest-deep)]/55 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--cream)]/85">
                  {t.methodology.imgCaption}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {t.pillars.map((p) => {
              const Icon = p.icon;
              return (
                <Card
                  key={p.id}
                  className="p-8 md:p-10 border border-[var(--forest-deep)]/10 bg-white hover:shadow-[0_20px_50px_-20px_rgb(15,61,35,0.15)] hover:border-[var(--forest)]/25 transition-all duration-300 group"
                >
                  <div className="flex items-start gap-5 mb-6">
                    <div className="shrink-0 h-14 w-14 rounded-2xl bg-[var(--forest)]/8 flex items-center justify-center group-hover:bg-[var(--forest)]/12 transition-colors">
                      <Icon size={26} className="text-[var(--forest)]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-mono-num font-semibold tracking-[0.15em] text-[var(--bronze)]">
                        {p.number}
                      </div>
                      <h3 className="font-display text-2xl md:text-3xl font-medium text-[var(--forest-deep)] mt-1 leading-tight">
                        {p.title}
                      </h3>
                    </div>
                  </div>
                  <p className="font-display text-lg italic text-[var(--forest)] mb-4 leading-snug">
                    {p.subtitle}
                  </p>
                  <p className="text-[15px] text-[var(--forest-deep)]/70 leading-relaxed mb-5">
                    {p.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {p.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-[11px] font-medium tracking-wide rounded-full bg-[var(--forest-deep)]/5 text-[var(--forest-deep)]/70"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Proceso */}
          <div className="mt-20 pt-20 border-t border-[var(--forest-deep)]/10">
            <div className="mb-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6">
                <div className="relative aspect-[3/2] rounded-2xl overflow-hidden shadow-[0_20px_60px_-25px_rgb(15,61,35,0.3)]">
                  <img
                    src={IMG_PROCESS_TEAM}
                    alt={t.methodology.processImgAlt}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
              </div>
              <div className="lg:col-span-6">
                <h3 className="font-display text-3xl md:text-4xl font-medium text-[var(--forest-deep)] leading-tight">
                  {t.methodology.processTitleA}
                  <span className="italic text-[var(--forest)]">{t.methodology.processTitleEm}</span>
                  {t.methodology.processTitleB}
                </h3>
                <p className="mt-5 text-lg text-[var(--forest-deep)]/70 leading-relaxed">
                  {t.methodology.processIntro}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.methodology.steps.map((s) => (
                <div key={s.step}>
                  <div className="font-display text-5xl font-light text-[var(--bronze)] mb-3">
                    {s.step}
                  </div>
                  <h3 className="font-display text-2xl font-medium text-[var(--forest-deep)] mb-3">
                    {s.title}
                  </h3>
                  <p className="text-[15px] text-[var(--forest-deep)]/70 leading-relaxed">
                    {s.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          TECNOLOGÍA
      ============================================ */}
      <section
        id="tecnologia"
        className="py-24 md:py-32 bg-[var(--forest-deep)] text-[var(--cream)] relative overflow-hidden"
        style={{ scrollMarginTop: 96 }}
      >
        <div className="absolute inset-0 -z-0">
          <img
            src={IMG_TECHNOLOGY}
            alt=""
            className="w-full h-full object-cover opacity-60 object-[center_30%]"
            draggable={false}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,61,35,0.85) 0%, rgba(15,61,35,0.35) 45%, rgba(15,61,35,0.75) 100%)",
            }}
          />
          <div
            className="absolute inset-0 hidden md:block"
            style={{
              background:
                "linear-gradient(90deg, rgba(15,61,35,0.6) 0%, rgba(15,61,35,0.1) 55%, rgba(15,61,35,0) 100%)",
            }}
          />
        </div>
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 10% 20%, oklch(0.9 0.1 85), transparent 50%)",
          }}
        />
        <div className="container relative">
          <div className="max-w-3xl mb-16">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--bronze-light)] mb-4">
              {t.technology.eyebrow}
            </div>
            <h2
              className="font-display font-light leading-[1.05] tracking-[-0.02em]"
              style={{ fontSize: "clamp(2.25rem, 5vw, 3.75rem)" }}
            >
              {t.technology.titleA}
              <span className="italic text-[var(--bronze-light)]">{t.technology.titleEm}</span>.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.tech.map((item) => (
              <div
                key={item.name}
                className="p-8 rounded-2xl bg-[var(--cream)]/5 border border-[var(--cream)]/10 backdrop-blur-sm"
              >
                <Gauge size={26} className="text-[var(--bronze-light)] mb-5" />
                <h3 className="font-display text-2xl font-medium mb-2">{item.name}</h3>
                <div className="text-xs font-semibold tracking-wide uppercase text-[var(--bronze-light)]/80 mb-4">
                  {item.role}
                </div>
                <p className="text-sm text-[var(--cream)]/75 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          EQUIPO (teaser — full team lives on /equipo)
      ============================================ */}
      <section id="equipo" className="py-24 md:py-32 bg-[var(--forest)]/4" style={{ scrollMarginTop: 96 }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-4">
                {t.team.eyebrow}
              </div>
              <h2
                className="font-display font-light text-[var(--forest-deep)] leading-[1.05] tracking-[-0.02em]"
                style={{ fontSize: "clamp(2.25rem, 5vw, 3.75rem)" }}
              >
                {t.team.titleA}
                <span className="italic text-[var(--forest)]">{t.team.titleEm}</span>.
              </h2>
              <p className="mt-6 text-lg text-[var(--forest-deep)]/70 leading-relaxed max-w-xl">
                {t.team.teaserIntro}
              </p>
              <Link
                href="/equipo"
                className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 bg-[var(--forest)] text-[var(--cream)] rounded-full font-semibold tracking-wide hover:bg-[var(--forest-deep)] transition-colors"
              >
                {t.team.teaserCta}
                <ArrowRight size={18} />
              </Link>
              <p className="mt-10 pt-8 border-t border-[var(--forest-deep)]/10 font-display italic text-[var(--forest)] leading-snug text-xl md:text-2xl max-w-lg">
                {t.quote}
              </p>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <Link href="/equipo" className="block group">
                <div className="overflow-hidden rounded-2xl border border-[var(--forest-deep)]/10 shadow-[0_20px_60px_-25px_rgb(15,61,35,0.3)]">
                  <img
                    src={IMG_TEAM_GROUP}
                    alt={t.team.groupAlt}
                    className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          TESTIMONIOS
      ============================================ */}
      <section
        id="testimonios"
        className="py-24 md:py-32 bg-[var(--forest-deep)] text-[var(--cream)]"
        style={{ scrollMarginTop: 96 }}
      >
        <div className="container">
          <div className="max-w-3xl mb-16">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--bronze-light)] mb-4">
              {t.testimonialsSection.eyebrow}
            </div>
            <h2
              className="font-display font-light leading-[1.05] tracking-[-0.02em]"
              style={{ fontSize: "clamp(2.25rem, 5vw, 3.75rem)" }}
            >
              {t.testimonialsSection.titleA}
              <span className="italic text-[var(--bronze-light)]">{t.testimonialsSection.titleEm}</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex flex-col rounded-2xl overflow-hidden bg-[var(--cream)]/5 border border-[var(--cream)]/10 shadow-[0_30px_70px_-30px_rgb(0,0,0,0.5)]"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={item.photo}
                    alt={`${item.name}, ${item.role}`}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
                <figcaption className="flex flex-col flex-1 p-7">
                  <blockquote className="text-[15px] leading-relaxed text-[var(--cream)]/85 italic flex-1">
                    “{item.quote}”
                  </blockquote>
                  <div className="mt-6 pt-5 border-t border-[var(--cream)]/15">
                    <div className="font-display text-lg font-medium text-[var(--cream)]">{item.name}</div>
                    <div className="text-xs text-[var(--bronze-light)] mt-1">{item.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          COLABORADORES
      ============================================ */}
      <section id="colaboradores" className="py-20 md:py-28" style={{ scrollMarginTop: 96 }}>
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-4">
              {t.collaborators.eyebrow}
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-[var(--forest-deep)] leading-tight">
              {t.collaborators.title}
            </h2>
            <p className="mt-4 text-[var(--forest-deep)]/70 leading-relaxed">
              {t.collaborators.intro}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {COLLABORATORS.map((c) => (
              <a
                key={c.name}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${c.name}`}
                className="group flex items-center justify-center rounded-2xl border border-[var(--forest-deep)]/10 bg-white p-10 transition-all hover:border-[var(--forest)]/40 hover:shadow-md"
              >
                <img
                  src={c.logo}
                  alt={c.name}
                  className="max-h-28 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          FAQ
      ============================================ */}
      <section id="faq" className="py-24 md:py-32" style={{ scrollMarginTop: 96 }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-4">
                <HelpCircle size={14} />
                {t.faqSection.eyebrow}
              </div>
              <h2
                className="font-display font-light text-[var(--forest-deep)] leading-[1.05] tracking-[-0.02em]"
                style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}
              >
                {t.faqSection.titleA}
                <span className="italic text-[var(--forest)]">{t.faqSection.titleEm}</span>
              </h2>
              <p className="mt-6 text-base text-[var(--forest-deep)]/75 leading-relaxed max-w-md">
                {t.faqSection.intro}
              </p>
              <div className="mt-8 rounded-xl border border-[var(--forest-deep)]/10 bg-[var(--cream)] p-6">
                <p className="text-sm text-[var(--forest-deep)]/80 leading-relaxed">
                  {t.faqSection.helpBox}
                </p>
                <a
                  href="#contacto"
                  className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-[var(--forest)] hover:text-[var(--forest-deep)] transition-colors"
                >
                  {t.faqSection.helpLink}
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
            <div className="lg:col-span-7">
              <Accordion type="single" collapsible className="w-full">
                {t.faqs.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="border-b border-[var(--forest-deep)]/10"
                  >
                    <AccordionTrigger className="text-left font-display text-lg md:text-xl text-[var(--forest-deep)] hover:no-underline py-6">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[var(--forest-deep)]/75 text-base leading-relaxed pb-6">
                      {item.a.split(/(\*\*[^*]+\*\*)/g).map((part, idx) =>
                        part.startsWith("**") && part.endsWith("**") ? (
                          <strong key={idx} className="text-[var(--forest-deep)] font-semibold">
                            {part.slice(2, -2)}
                          </strong>
                        ) : (
                          <span key={idx}>{part}</span>
                        ),
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          CONTACTO
      ============================================ */}
      <section
        id="contacto"
        className="py-24 md:py-32 bg-[var(--forest)]/4 relative"
        style={{ scrollMarginTop: 96 }}
      >
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-4">
                {t.contact.eyebrow}
              </div>
              <h2
                className="font-display font-light text-[var(--forest-deep)] leading-[1.05] tracking-[-0.02em]"
                style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}
              >
                {t.contact.titleA}<span className="italic text-[var(--forest)]">{t.contact.titleEm}</span>.
              </h2>
              <p className="mt-6 text-lg text-[var(--forest-deep)]/70 leading-relaxed">
                {t.contact.intro}
              </p>

              <div className="mt-10 space-y-5">
                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex items-center gap-4 group"
                >
                  <div className="h-12 w-12 rounded-xl bg-[var(--forest)]/10 flex items-center justify-center group-hover:bg-[var(--forest)]/15 transition-colors">
                    <Mail size={20} className="text-[var(--forest)]" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold tracking-wide uppercase text-[var(--forest-deep)]/60">{t.contact.emailLabel}</div>
                    <div className="text-[var(--forest-deep)] font-medium">{BRAND.email}</div>
                  </div>
                </a>

                <a
                  href={BRAND.whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <div className="h-12 w-12 rounded-xl bg-[var(--forest)]/10 flex items-center justify-center group-hover:bg-[var(--forest)]/15 transition-colors">
                    <MessageCircle size={20} className="text-[var(--forest)]" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold tracking-wide uppercase text-[var(--forest-deep)]/60">{t.contact.whatsappLabel}</div>
                    <div className="text-[var(--forest-deep)] font-medium">{BRAND.whatsappDisplay}</div>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-[var(--forest)]/10 flex items-center justify-center">
                    <MapPin size={20} className="text-[var(--forest)]" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold tracking-wide uppercase text-[var(--forest-deep)]/60">{t.contact.locationLabel}</div>
                    <div className="text-[var(--forest-deep)] font-medium">{t.contact.locationValue}</div>
                  </div>
                </div>

                <div className="mt-2 rounded-2xl overflow-hidden border border-[var(--forest-deep)]/10 shadow-sm">
                  <iframe
                    title={t.contact.mapTitle}
                    src="https://www.google.com/maps?q=Albacete,%20España&z=12&output=embed"
                    className="w-full h-56 md:h-64"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>

            <div id="formulario" className="lg:col-span-7" style={{ scrollMarginTop: 96 }}>
              <Card className="p-8 md:p-10 border border-[var(--forest-deep)]/10 bg-white">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-xs font-semibold tracking-wide uppercase">
                        {t.contact.form.name}
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="mt-2 h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-xs font-semibold tracking-wide uppercase">
                        {t.contact.form.phone}
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="mt-2 h-12"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="text-xs font-semibold tracking-wide uppercase">
                        {t.contact.form.email}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="mt-2 h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="handicap" className="text-xs font-semibold tracking-wide uppercase">
                        {t.contact.form.handicap}
                      </Label>
                      <Input
                        id="handicap"
                        name="handicap"
                        value={form.handicap}
                        onChange={(e) => setForm({ ...form, handicap: e.target.value })}
                        placeholder={t.contact.form.handicapPlaceholder}
                        className="mt-2 h-12"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="playerProfile" className="text-xs font-semibold tracking-wide uppercase">
                      {t.contact.form.playerProfile}
                    </Label>
                    <Select
                      value={form.playerProfile}
                      onValueChange={(value) => setForm({ ...form, playerProfile: value as PlayerProfile })}
                    >
                      <SelectTrigger id="playerProfile" className="mt-2 data-[size=default]:h-12">
                        <SelectValue placeholder={t.contact.form.playerProfilePlaceholder} />
                      </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="estancado">{t.contact.form.profiles.estancado}</SelectItem>
                          <SelectItem value="dolor">{t.contact.form.profiles.dolor}</SelectItem>
                          <SelectItem value="competidor">{t.contact.form.profiles.competidor}</SelectItem>
                          <SelectItem value="profesional">{t.contact.form.profiles.profesional}</SelectItem>
                          <SelectItem value="otro">{t.contact.form.profiles.otro}</SelectItem>
                        </SelectContent>
                      </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-xs font-semibold tracking-wide uppercase">
                      {t.contact.form.message}
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="mt-2"
                      placeholder={t.contact.form.messagePlaceholder}
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent"
                      checked={form.consent}
                      onCheckedChange={(v: boolean | "indeterminate") =>
                        setForm({ ...form, consent: v === true })
                      }
                      className="mt-0.5 shrink-0"
                    />
                    <label
                      htmlFor="consent"
                      className="text-xs text-[var(--forest-deep)]/70 leading-relaxed cursor-pointer"
                    >
                      {t.contact.form.consentPre}
                      <Link
                        href="/privacidad"
                        target="_blank"
                        className="underline hover:text-[var(--forest)]"
                      >
                        {t.contact.form.consentLink}
                      </Link>
                      {t.contact.form.consentPost}
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting || !form.consent}
                    className="w-full h-12 bg-[var(--forest)] hover:bg-[var(--forest-deep)] text-[var(--cream)] text-base font-semibold disabled:opacity-50"
                  >
                    {submitting ? t.contact.form.submitting : t.contact.form.submit}
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
      <WhatsAppButton />
    </div>
  );
}
